import React, { useCallback } from 'react';

import { useIntl } from 'react-intl-next';
import { useFragment } from 'react-relay';
import { graphql } from 'relay-runtime';

import type { DocNode } from '@atlaskit/adf-schema';
import { CommonHubExtensionsDependenciesProvider } from '@atlassian/common-hub-extensions-dependencies-provider';
import { helpCenterTypeToProduct, useHubContext } from '@atlassian/company-hub-common';
import HubEditor, { type HubEditorPresetAPI } from '@atlassian/hub-editor';

import { useLinkPickerPlugins } from '../../../controllers/use-link-picker-plugins';
import { useProductExtensions } from '../../../controllers/use-product-extensions';

import { type layoutEditor_AtlassianHubHome$key } from './__generated__/layoutEditor_AtlassianHubHome.graphql';

export const LayoutEditor = ({
	layoutEditorQueryReference,
	handleEditorChange,
	setEditorApi,
	setCurrentEditorContent,
	helpCenterAri,
}: {
	handleEditorChange: () => void;
	helpCenterAri: string;
	layoutEditorQueryReference: layoutEditor_AtlassianHubHome$key;
	setCurrentEditorContent: (content: DocNode | null) => void;
	setEditorApi?: (editorApi: HubEditorPresetAPI | null) => void;
}): React.JSX.Element => {
	const intl = useIntl();
	const { cloudId, cloudUrl } = useHubContext();

	const data = useFragment<layoutEditor_AtlassianHubHome$key>(
		graphql`
			fragment layoutEditor_AtlassianHubHome on HelpCenterQueryApi {
				helpCenterById(helpCenterAri: $hubAri) @optIn(to: "HelpCenterAggBeta") {
					__typename
					... on HelpCenter {
						slug @optIn(to: "HelpCenterSlugTest")
						type @optIn(to: "HelpCenterTypeTest")
						currentPage: pages(filter: { ids: [$pageAri] }) @required(action: THROW) {
							id
							pageLayout {
								layoutAdf {
									content {
										edges {
											node {
												content
											}
										}
									}
								}
							}
						}
					}
					... on QueryError {
						message
					}
				}
			}
		`,
		layoutEditorQueryReference,
	);

	const helpCenterData = data.helpCenterById;

	const getParsedAdf = useCallback(() => {
		const layoutAdf =
			helpCenterData?.__typename === 'HelpCenter'
				? helpCenterData.currentPage?.[0]?.pageLayout?.layoutAdf?.content?.edges
						?.map((edge: any) => edge?.node?.content)
						?.join('') || '{}'
				: '{}';

		const parsedAdf = JSON.parse(layoutAdf) as DocNode;

		return parsedAdf;
	}, [helpCenterData]);

	React.useEffect(() => {
		setCurrentEditorContent(getParsedAdf());
	}, [helpCenterData, setCurrentEditorContent, getParsedAdf]);

	if (helpCenterData?.__typename === 'QueryError') {
		throw new Error(`QueryError in hub query: ${helpCenterData.message}`);
	}

	if (helpCenterData?.__typename !== 'HelpCenter') {
		throw new Error('HelpCenter not found in hub query');
	}
	if (helpCenterData?.currentPage === null) {
		throw new Error('HelpCenterPages not found in hub query');
	}

	const helpCenterType = helpCenterData?.type || '';
	// Get additional extensions for the product
	const { extensionProviders, loadExtensionProviders } = useProductExtensions({
		contentId: helpCenterData?.currentPage?.[0]?.id ?? '',
		intl,
		PropsProvider: CommonHubExtensionsDependenciesProvider,
		product: helpCenterType,
	});

	const helpCenterSlug = helpCenterData?.slug || '';
	const baseUrl = `${cloudUrl}/helpcenter/${helpCenterSlug}`;
	const linkPickerOptions = useLinkPickerPlugins({
		cloudId,
		helpCenterAri,
		baseUrl,
		helpCenterType,
	});

	return (
		<HubEditor
			key={helpCenterData?.currentPage?.[0]?.id}
			shouldFocus
			appearance="full-page"
			defaultValue={getParsedAdf() || {}}
			onChange={handleEditorChange}
			product={helpCenterTypeToProduct(helpCenterType)}
			hasEditPermission
			viewMode={'edit'}
			userPreferencesProvider={undefined}
			userPreferencesProviderNext={undefined}
			isLoomPluginEnabled={false}
			editorPluginAIOptions={undefined}
			editorPluginAIDefinitionsEnabled={false}
			editorPluginAIDefinitionsConfig={undefined}
			isDesktop={false}
			contextPanelOptions={undefined}
			getExtensionHeight={undefined}
			__rendererExtensionOptions={undefined}
			contentId={helpCenterData?.currentPage?.[0]?.id || ''}
			linkPickerOptions={linkPickerOptions}
			setExternalEditorApi={setEditorApi}
			additionalExtensionProviders={extensionProviders}
			loadAdditionalExtensionProviders={loadExtensionProviders}
			PropsProvider={CommonHubExtensionsDependenciesProvider}
			editorPluginHubTextColorEnabled={true}
		/>
	);
};
